import React from 'react';
import dayjs from 'dayjs';
import { DatePicker, Input, Select, Radio, Button } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  DEFAULT_OPERATORS,
  getEmptyConditionValue,
  getGroupRowFields,
  getOperatorValueKind,
  isConditionValueMissing,
  isMonthCondition,
} from './conditionOperators';

const { Option } = Select;

const selectedStyle = {
  borderColor: '#FA8C16',
  boxShadow: 'none',
  background: '#fff',
  color: '#FA8C16',
  fontWeight: 400,
};

const selectedPurpleStyle = {
  borderColor: '#9254DE',
  boxShadow: 'none',
  background: '#fff',
  color: '#9254DE',
  fontWeight: 400,
};

const unselectedStyle = {
  borderColor: '#D9D9D9',
  boxShadow: 'none',
  background: '#fff',
  color: 'rgba(0, 0, 0, 0.88)',
};

const NUMBER_TYPES = ['integer', 'number'];
// Date and datetime fields both take a date only (no time picker).
const DATE_TYPES = ['date', 'datetime'];
const DATE_FORMAT = 'YYYY-MM-DD';

// One value box, shaped by the field type (boolean / number / date / text).
const SingleValueInput = ({ fieldType, value, onChange, hasError, placeholder }) => {
  const status = hasError ? 'error' : undefined;

  if (fieldType === 'boolean') {
    return (
      <Select
        value={value === '' || value === undefined || value === null ? 'true' : String(value)}
        placeholder='Select'
        onChange={onChange}
        status={status}
        className='Input-border InputSelect condition-input w-120'
      >
        <Option value='true'>true</Option>
        <Option value='false'>false</Option>
      </Select>
    );
  }

  if (DATE_TYPES.includes(fieldType)) {
    const parsed = value ? dayjs(value) : null;
    return (
      <DatePicker
        value={parsed && parsed.isValid() ? parsed : null}
        format={DATE_FORMAT}
        onChange={(date) => onChange(date ? date.format(DATE_FORMAT) : '')}
        status={status}
        placeholder={placeholder || 'Select date'}
        className='Input-border InputText condition-input'
      />
    );
  }

  const isNumeric = NUMBER_TYPES.includes(fieldType);
  return (
    <Input
      value={value ?? ''}
      inputMode={isNumeric ? 'decimal' : undefined}
      onChange={(event) => {
        const nextValue = event.target.value;
        if (isNumeric && !/^-?\d*\.?\d*$/.test(nextValue)) return;
        onChange(nextValue);
      }}
      placeholder={placeholder || (isNumeric ? 'Number' : 'Input')}
      status={status}
      className='Input-border InputText condition-input w-120'
    />
  );
};

const ConditionRow = ({
  row,
  onChange,
  onDelete,
  canDeleteCondition,
  showValidation,
  fieldOptions = [],
  fieldsLoading = false,
  disabledFields = [],
}) => {
  const dotColor = row.type === 'And' ? '#9254DE' : '#FA8C16';
  const showError = showValidation && isConditionValueMissing(row);
  // A saved rule may already repeat a field; flag it so it gets changed.
  const isDuplicateField = Boolean(row.field) && disabledFields.includes(row.field);
  const fieldMeta = fieldOptions.find((option) => option.field === row.field);
  const fieldType = fieldMeta?.type;
  const operators = fieldMeta?.operators?.length ? fieldMeta.operators : DEFAULT_OPERATORS;
  const valueKind = getOperatorValueKind(row.operator);
  // Keep the saved field selectable even if it is missing from the fetched list.
  const options = row.field && !fieldOptions.some((option) => option.field === row.field)
    ? [{ field: row.field }, ...fieldOptions]
    : fieldOptions;
  // Keep a saved operator visible even if the field no longer allows it.
  const operatorOptions = row.operator && !operators.includes(row.operator) ? [row.operator, ...operators] : operators;

  const handleFieldChange = (field) => {
    // A new field has its own operators and value type, so start from its first operator.
    const nextField = fieldOptions.find((option) => option.field === field);
    const operator = nextField?.operators?.length ? nextField.operators[0] : '=';
    onChange({ field, operator, value: getEmptyConditionValue(nextField?.type, operator) });
  };

  const handleOperatorChange = (operator) => {
    // Keep the value when the value shape stays the same (e.g. '>=' to '<').
    const keepValue = getOperatorValueKind(operator) === valueKind;
    onChange(keepValue ? { operator } : { operator, value: getEmptyConditionValue(fieldType, operator) });
  };

  const renderValue = () => {
    if (valueKind === 'none') return null;

    if (valueKind === 'days') {
      return (
        <SingleValueInput
          fieldType='integer'
          value={row.value}
          onChange={(value) => onChange({ value })}
          hasError={showError}
          placeholder={isMonthCondition(row.field, row.operator) ? 'Months' : 'Days'}
        />
      );
    }

    if (valueKind === 'list') {
      return (
        <Select
          mode='tags'
          value={Array.isArray(row.value) ? row.value : []}
          onChange={(value) => onChange({ value })}
          placeholder='Type and press Enter'
          tokenSeparators={[',']}
          status={showError ? 'error' : undefined}
          className='Input-border InputSelect condition-input'
          style={{ minWidth: 160 }}
        />
      );
    }

    if (valueKind === 'between') {
      const [from = '', to = ''] = Array.isArray(row.value) ? row.value : [];
      return (
        <div className='d-flex alignCenter gap-8'>
          <SingleValueInput
            fieldType={fieldType}
            value={from}
            onChange={(value) => onChange({ value: [value, to] })}
            hasError={showError && String(from).trim() === ''}
            placeholder='From'
          />
          <span>and</span>
          <SingleValueInput
            fieldType={fieldType}
            value={to}
            onChange={(value) => onChange({ value: [from, value] })}
            hasError={showError && String(to).trim() === ''}
            placeholder='To'
          />
        </div>
      );
    }

    return (
      <SingleValueInput
        fieldType={fieldType}
        value={row.value}
        onChange={(value) => onChange({ value })}
        hasError={showError}
      />
    );
  };

  return (
    <div className='condition-row d-flex alignCenter gap-8'>
      <span className='condition-dot' style={{ background: dotColor }} />
      <Select
        value={row.field || undefined}
        placeholder='Search field'
        showSearch
        loading={fieldsLoading}
        // Case-insensitive; spaces match underscores, so "last order" finds online_last_order_date.
        filterOption={(input, option) => String(option?.value ?? '').toLowerCase()
          .includes(input.trim().toLowerCase().replace(/\s+/g, '_'))}
        popupMatchSelectWidth={false}
        onChange={handleFieldChange}
        status={isDuplicateField ? 'error' : undefined}
        className='Input-border InputSelect condition-select w-85'
      >
        {options.map((option) => (
          <Option
            key={option.field}
            value={option.field}
            disabled={option.field !== row.field && disabledFields.includes(option.field)}
          >
            {option.field}
          </Option>
        ))}
      </Select>
      <Select
        value={row.operator || undefined}
        onChange={handleOperatorChange}
        popupMatchSelectWidth={false}
        // Word operators (e.g. "within_n_days") need more room than "=".
        style={{ minWidth: 64, width: String(row.operator || '').length > 2 ? 140 : undefined }}
        className='Input-border InputSelect condition-operator w-64'
      >
        {operatorOptions.map((operator) => (
          <Option key={operator} value={operator}>{operator}</Option>
        ))}
      </Select>
      <div className='d-flex d-flex-col gap-4 p-relative'>
        {valueKind !== 'none' && (
          <div className='d-flex alignCenter gap-4'>
            <span style={{ color: '#ff4d4f' }}>*</span>
            {renderValue()}
          </div>
        )}
        {showError && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Value is required.</div>}
        {isDuplicateField && (
          <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Attribute already used in this or a parent group.</div>
        )}
      </div>
      {canDeleteCondition && (
        <div className='condition-delete-box'>
          <button type='button' className='condition-delete-btn delete-btn' onClick={onDelete}>
            <DeleteOutlined style={{ color: '#FF4D4F' }} />
          </button>
        </div>
      )}
    </div>
  );
};

const EligibilityConditionGroup = ({
  group,
  onChangeType,
  onChangeCondition,
  onAddCondition,
  onAddNestedGroup,
  onRemoveGroup,
  canRemoveGroup,
  onRemoveCondition,
  children,
  showValidation = false,
  fieldOptions = [],
  fieldsLoading = false,
  blockedFields = [],
}) => {
  const groupText = group.type === 'And'
    ? '(All of the following must be true)'
    : '(Any one of the following must be true)';
  const canDeleteCondition = group.conditions.length + (group.afterConditions || []).length > 1;

  // Parent/nested fields plus the fields of the other rows in this group.
  const disabledFieldsFor = (row) => [
    ...blockedFields,
    ...getGroupRowFields({
      conditions: group.conditions.filter((other) => other.id !== row.id),
      afterConditions: (group.afterConditions || []).filter((other) => other.id !== row.id),
    }),
  ];

  return (
    <div className='condition-border-box condition-box mt-16 nested-condition-group'>
      <div className='attribute-condition-card'>
        <div className='condition-wrapper'>
          <div className='condition-connector'>
            <div className='condition-connector-header'>
              <div className='RadioGroup d-flex justifyContentSpaceBetween alignCenter'>
                <Radio.Group
                  value={group.type}
                  onChange={(e) => onChangeType(group.id, e.target.value)}
                  className='RadioGroup-inline'
                >
                  <Radio.Button
                    value='And'
                    style={group.type === 'And'
                      ? { ...selectedPurpleStyle, '--radio-checked-color': '#9254DE' }
                      : { ...unselectedStyle, '--radio-checked-color': '#D9D9D9' }}
                  >
                    And
                  </Radio.Button>
                  <Radio.Button
                    value='Or'
                    style={group.type === 'Or'
                      ? { ...selectedStyle, '--radio-checked-color': '#FA8C16' }
                      : { ...unselectedStyle, '--radio-checked-color': '#D9D9D9' }}
                  >
                    Or
                  </Radio.Button>
                </Radio.Group>

                {canRemoveGroup && (
                  <button type='button' className='condition-delete-btn delete-btn' onClick={() => onRemoveGroup(group.id)}>
                    <DeleteOutlined style={{ color: '#FF4D4F' }} />
                    <span style={{ marginLeft: 8, color: '#FF4D4F' }}>Remove group</span>
                  </button>
                )}
              </div>
              <span className='RadioGroup-text'>{groupText}</span>
            </div>
          </div>

          {group.conditions.map((row) => (
            <ConditionRow
              key={row.id}
              row={{ ...row, type: group.type }}
              canDeleteCondition={canDeleteCondition}
              onChange={(changes) => onChangeCondition(group.id, row.id, changes)}
              onDelete={() => onRemoveCondition(group.id, row.id)}
              showValidation={showValidation}
              fieldOptions={fieldOptions}
              fieldsLoading={fieldsLoading}
              disabledFields={disabledFieldsFor(row)}
            />
          ))}
        </div>

        {children && <div className='nested-group-list'>{children}</div>}

        {(group.afterConditions || []).map((row) => (
          <ConditionRow
            key={row.id}
            row={{ ...row, type: group.type }}
            canDeleteCondition={canDeleteCondition}
            onChange={(changes) => onChangeCondition(group.id, row.id, changes)}
            onDelete={() => onRemoveCondition(group.id, row.id)}
            showValidation={showValidation}
            fieldOptions={fieldOptions}
            fieldsLoading={fieldsLoading}
            disabledFields={disabledFieldsFor(row)}
          />
        ))}

        <div className='condition-footer mt-16'>
          <div className='footer-btn-box d-flex gap-8'>
            <Button type='default' icon={<PlusOutlined />} onClick={() => onAddCondition(group.id)}>
              Add Condition
            </Button>
            <Button type='default' icon={<PlusOutlined />} onClick={() => onAddNestedGroup(group.id)}>
              Add Nested Group
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EligibilityConditionGroup;
